(function () {
  const ROOT_ATTR = 'data-wl-klaviyo-popup-root';
  const PANEL_ATTR = 'data-wl-klaviyo-popup-panel';
  const FORM_ATTR = 'data-wl-klaviyo-popup-form';
  const OVERLAY_ATTR = 'data-wl-klaviyo-popup-overlay';
  const INPUT_ATTR = 'data-wl-klaviyo-popup-input';
  const INPUT_WRAP_ATTR = 'data-wl-klaviyo-popup-input-wrap';
  const SUBMIT_ATTR = 'data-wl-klaviyo-popup-submit';
  const CLOSE_ATTR = 'data-wl-klaviyo-popup-close';
  const DISMISS_ATTR = 'data-wl-klaviyo-popup-dismiss';
  const SUPPRESSED_ATTR = 'data-wl-klaviyo-popup-suppressed';
  const SUPPRESSION_REASON_ATTR = 'data-wl-klaviyo-popup-suppression-reason';
  const ALLOWED_ATTR = 'data-wl-klaviyo-popup-allowed';
  const CART_DRAWER_OPEN_CLASS = 'wl-cart-drawer-open';
  const CART_INTERACTION_CLASS = 'wl-cart-interaction-active';
  const POPUP_CRITICAL_CLASS = 'wl-popup-critical-active';
  const POPUP_DELAY_CLASS = 'wl-popup-delay-active';
  const CART_INTERACTION_SUPPRESSION_MS = 30000;
  const CART_DRAWER_CLOSE_SUPPRESSION_MS = 15000;
  const CHECKOUT_SUPPRESSION_MS = 120000;
  const TEASER_REOPEN_WINDOW_MS = 8000;
  const INITIAL_POPUP_DELAY_MS = window.matchMedia('(max-width: 749px)').matches ? 15000 : 10000;
  const POPUP_SESSION_LIMIT_KEY = 'wlKlaviyoPopupShownAt';
  const MENU_DRAWER_SELECTOR = '#Details-menu-drawer-container[open]';
  const BROWSING_OVERLAY_SELECTOR = [
    MENU_DRAWER_SELECTOR,
    'modal-dialog[open]',
    'quick-add-modal[open]',
    'pickup-availability-drawer[open]',
    '.product-popup-modal[open]',
    '.media-modal[open]',
    '.facets__disclosure[open]',
  ].join(',');
  const SHOPPING_TRIGGER_SELECTOR = [
    '#cart-icon-bubble',
    'product-form',
    '.product-form__submit',
    '.quick-add__submit',
    'quick-add-bulk',
    'bulk-add',
    'cart-drawer',
    'cart-notification',
    'cart-items',
    'cart-remove-button',
    'quantity-input',
    '.cart__ctas',
    '.cart__checkout-button',
    '.cart__dynamic-checkout-buttons',
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    '[data-cart-upsell-form]',
    'wl-product-accessories',
    'wl-product-bundle-builder',
    'modal-opener[data-modal*="QuickAdd"]',
  ].join(',');
  const HINTS = ['klaviyo', 'kl-private-reset-css', 'needsclick'];
  const suppressedRootState = new WeakMap();
  const popupVisibilityState = new WeakMap();
  const allowedRoots = new WeakSet();
  const startedAt = Date.now();
  let criticalSuppressionUntil = 0;
  let checkoutSuppressionUntil = 0;
  let criticalSuppressionTimeout = null;
  let delayReleaseTimeout = null;
  let teaserReopenUntil = 0;
  let scheduled = false;

  function getSignature(element) {
    if (!(element instanceof Element)) return '';

    return [
      element.id,
      typeof element.className === 'string' ? element.className : '',
      element.getAttribute('role'),
      element.getAttribute('aria-label'),
      element.getAttribute('data-testid'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  function hasKlaviyoHint(element) {
    let current = element;

    while (current && current !== document.body) {
      const signature = getSignature(current);
      if (HINTS.some((hint) => signature.includes(hint))) return true;
      current = current.parentElement;
    }

    return false;
  }

  function isPopupForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.closest('.wl-footer__klaviyo, [data-wl-klaviyo-embed]')) return false;

    const emailInput = form.querySelector("input[type='email'], input[name*='email' i]");
    const submitButton = form.querySelector("button[type='submit'], button, input[type='submit']");
    if (!emailInput || !submitButton) return false;

    if (hasKlaviyoHint(form)) return true;

    let current = form;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      if (style.position === 'fixed') return true;
      current = current.parentElement;
    }

    return false;
  }

  function findPanel(form) {
    let current = form;
    let candidate = form;

    while (current.parentElement && current.parentElement !== document.body) {
      const parent = current.parentElement;
      const signature = getSignature(parent);
      const style = window.getComputedStyle(parent);
      const isDialogContainer = parent.matches('[role="dialog"], [aria-modal="true"]');
      const hasPopupHint =
        signature.includes('klaviyo') ||
        signature.includes('kl-private-reset-css') ||
        signature.includes('needsclick');

      if (isDialogContainer || (hasPopupHint && style.position !== 'fixed')) {
        candidate = parent;
      }

      if (style.position === 'fixed') break;
      current = parent;
    }

    return candidate;
  }

  function findRoot(panel) {
    let current = panel;
    let candidate = panel;

    while (current.parentElement && current.parentElement !== document.body) {
      const parent = current.parentElement;
      const style = window.getComputedStyle(parent);

      if (style.position === 'fixed' || hasKlaviyoHint(parent)) {
        candidate = parent;
      }

      current = parent;
    }

    return candidate;
  }

  function findOverlay(root, panel) {
    let current = panel.parentElement;

    while (current && current !== root && current !== document.body) {
      const style = window.getComputedStyle(current);
      if (style.position === 'fixed') return current;
      current = current.parentElement;
    }

    return null;
  }

  function getControlText(control) {
    if (!(control instanceof Element)) return '';

    return [
      control.getAttribute('aria-label'),
      control.getAttribute('title'),
      control.value,
      control.textContent,
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
      .toLowerCase();
  }

  function findInputWrapper(form, input) {
    let current = input;

    while (current.parentElement && current.parentElement !== form) {
      const parent = current.parentElement;
      const siblingControls = Array.from(parent.querySelectorAll("input, button, textarea, select")).filter(
        (node) => node !== input
      );

      if (siblingControls.length > 0) break;
      current = parent;
    }

    return current;
  }

  function isLikelyCloseControl(control) {
    if (!(control instanceof HTMLElement)) return false;
    const text = getControlText(control);
    if (/(^|\b)(close|dismiss)(\b|$)/.test(text)) return true;
    if (/^[x×✕]$/.test(text)) return true;

    const style = window.getComputedStyle(control);
    return (
      (style.position === 'absolute' || style.position === 'fixed') &&
      parseFloat(style.top || '999') < 40 &&
      parseFloat(style.right || '999') < 40
    );
  }

  function isRenderedVisible(element) {
    if (!(element instanceof HTMLElement)) return false;

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity || '1') > 0
    );
  }

  function focusPopupOnOpen(panel, emailInput) {
    const isOpen = isRenderedVisible(panel);
    const wasOpen = popupVisibilityState.get(panel) === true;
    popupVisibilityState.set(panel, isOpen);

    if (!isOpen || wasOpen || !(emailInput instanceof HTMLElement) || panel.contains(document.activeElement)) return;

    window.requestAnimationFrame(() => {
      if (!isRenderedVisible(panel) || panel.contains(document.activeElement)) return;
      emailInput.focus({ preventScroll: true });
    });
  }

  function markPopup(form) {
    if (!isPopupForm(form)) return;

    const panel = findPanel(form);
    const root = findRoot(panel);
    const overlay = findOverlay(root, panel);
    const emailInput = form.querySelector("input[type='email'], input[name*='email' i]");
    const inputWrapper = emailInput ? findInputWrapper(form, emailInput) : null;
    const submitControls = form.querySelectorAll("button[type='submit'], input[type='submit']");

    form.setAttribute(FORM_ATTR, '');
    panel.setAttribute(PANEL_ATTR, '');
    root.setAttribute(ROOT_ATTR, '');
    if (overlay && overlay !== panel) overlay.setAttribute(OVERLAY_ATTR, '');

    if (emailInput) {
      emailInput.setAttribute(INPUT_ATTR, '');
      if (inputWrapper && inputWrapper !== emailInput) inputWrapper.setAttribute(INPUT_WRAP_ATTR, '');
    }

    submitControls.forEach((control) => control.setAttribute(SUBMIT_ATTR, ''));

    panel.querySelectorAll('button, a[href], [role="button"]').forEach((control) => {
      if (!(control instanceof HTMLElement)) return;
      if (form.contains(control)) return;

      control.removeAttribute(CLOSE_ATTR);
      control.removeAttribute(DISMISS_ATTR);

      if (isLikelyCloseControl(control)) {
        control.setAttribute(CLOSE_ATTR, '');
      } else {
        control.setAttribute(DISMISS_ATTR, '');
      }
    });

    focusPopupOnOpen(panel, emailInput);
    updatePopupSuppression();
  }

  function getPathname(url) {
    try {
      return new URL(url, window.location.origin).pathname.toLowerCase();
    } catch (error) {
      return String(url || '').toLowerCase();
    }
  }

  function pathMatchesRoute(path, route) {
    const routePath = getPathname(route);
    return path === routePath || path.startsWith(`${routePath}/`);
  }

  function getPathSegments(path = window.location.pathname) {
    return String(path || '')
      .toLowerCase()
      .split('/')
      .filter(Boolean);
  }

  function isCartDrawerOpen() {
    return (
      document.body.classList.contains(CART_DRAWER_OPEN_CLASS) ||
      document.documentElement.classList.contains(CART_DRAWER_OPEN_CLASS) ||
      Boolean(document.querySelector('cart-drawer.active, cart-drawer .drawer.active'))
    );
  }

  function isCartNotificationOpen() {
    return Boolean(document.querySelector('#cart-notification.active, cart-notification .cart-notification.active'));
  }

  function isBrowsingOverlayOpen() {
    return Boolean(document.querySelector(BROWSING_OVERLAY_SELECTOR));
  }

  function isCartPage() {
    const path = window.location.pathname.toLowerCase();
    return (
      pathMatchesRoute(path, window.routes?.cart_url || '/cart') ||
      getPathSegments(path).includes('cart') ||
      document.body.className.includes('template-cart')
    );
  }

  function isCheckoutPage() {
    const path = window.location.pathname.toLowerCase();
    const segments = getPathSegments(path);
    return segments.includes('checkout') || segments.includes('checkouts');
  }

  function isHighIntentShoppingPage() {
    const segments = getPathSegments();
    return segments.includes('products') || segments.includes('collections') || segments.includes('search');
  }

  function isInitialDelayActive() {
    return Date.now() - startedAt < INITIAL_POPUP_DELAY_MS;
  }

  function isTimedCriticalSuppressionActive() {
    const now = Date.now();
    return now < criticalSuppressionUntil || now < checkoutSuppressionUntil;
  }

  function getRemainingTimedSuppression() {
    const until = Math.max(criticalSuppressionUntil, checkoutSuppressionUntil);
    return Math.max(0, until - Date.now());
  }

  function hasPopupBeenShownThisSession() {
    try {
      return Boolean(window.sessionStorage.getItem(POPUP_SESSION_LIMIT_KEY));
    } catch (error) {
      return false;
    }
  }

  function recordPopupShown(root) {
    if (!(root instanceof HTMLElement) || allowedRoots.has(root)) return;

    allowedRoots.add(root);
    root.setAttribute(ALLOWED_ATTR, '');

    try {
      window.sessionStorage.setItem(POPUP_SESSION_LIMIT_KEY, String(Date.now()));
    } catch (error) {
      // Ignore storage failures; Klaviyo's own frequency rules still apply.
    }
  }

  function isSessionLimited(root) {
    return hasPopupBeenShownThisSession() && !allowedRoots.has(root);
  }

  function isTeaserReopenActive() {
    return Date.now() < teaserReopenUntil;
  }

  function isKlaviyoTeaserTrigger(target) {
    if (!(target instanceof Element)) return false;

    const trigger = target.closest('button, a, [role="button"], [tabindex], .needsclick');
    if (!trigger || trigger.closest(`[${ROOT_ATTR}], .wl-footer__klaviyo, [data-wl-klaviyo-embed]`)) return false;
    if (!hasKlaviyoHint(trigger)) return false;

    const text = getControlText(trigger);
    return /10%|\b(save|offer|discount|deal)\b/.test(text);
  }

  function handleTeaserActivation(event) {
    if (!isKlaviyoTeaserTrigger(event.target)) return;

    teaserReopenUntil = Date.now() + TEASER_REOPEN_WINDOW_MS;
    scheduleScan();
  }

  function formTargetsCartRoute(form) {
    if (!(form instanceof HTMLFormElement)) return false;

    const action = form.getAttribute('action') || window.location.pathname;
    const path = getPathname(action);
    const cartRoutes = [
      window.routes?.cart_add_url || '/cart/add',
      window.routes?.cart_change_url || '/cart/change',
      window.routes?.cart_update_url || '/cart/update',
      window.routes?.cart_url || '/cart',
      '/cart/clear',
      '/cart/add.js',
      '/cart/change.js',
      '/cart/update.js',
      '/cart.js',
    ];

    return cartRoutes.some((route) => pathMatchesRoute(path, route));
  }

  function isCartMutationForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.closest('product-form, [data-cart-upsell-form], wl-product-accessories, wl-product-bundle-builder')) {
      return true;
    }

    return formTargetsCartRoute(form);
  }

  function isCheckoutTrigger(trigger) {
    if (!(trigger instanceof Element)) return false;

    if (trigger.closest('[name="checkout"], .cart__checkout-button, .shopify-payment-button, .shopify-payment-button__button')) {
      return true;
    }

    const link = trigger.closest('a[href]');
    if (link && getPathname(link.getAttribute('href')).includes('/checkout')) return true;

    const form = trigger.closest('form');
    if (form && formTargetsCartRoute(form) && /checkout|shop pay|paypal|apple pay|google pay/.test(getControlText(trigger))) {
      return true;
    }

    return false;
  }

  function isShoppingTrigger(trigger) {
    if (!(trigger instanceof Element)) return false;
    if (isCheckoutTrigger(trigger)) return true;
    if (trigger.closest(SHOPPING_TRIGGER_SELECTOR)) return true;

    const form = trigger instanceof HTMLFormElement ? trigger : trigger.closest('form');
    if (isCartMutationForm(form)) return true;

    const link = trigger.closest('a[href]');
    if (link && pathMatchesRoute(getPathname(link.getAttribute('href')), window.routes?.cart_url || '/cart')) return true;

    const text = getControlText(trigger);
    return /\b(add to cart|buy now|checkout|view cart|cart)\b/.test(text);
  }

  function setPopupStateClasses() {
    const delayActive = isInitialDelayActive();
    const criticalActive = isTimedCriticalSuppressionActive();

    document.documentElement.classList.toggle(POPUP_DELAY_CLASS, delayActive);
    document.body.classList.toggle(POPUP_DELAY_CLASS, delayActive);
    document.documentElement.classList.toggle(CART_INTERACTION_CLASS, criticalActive);
    document.body.classList.toggle(CART_INTERACTION_CLASS, criticalActive);
    document.documentElement.classList.toggle(POPUP_CRITICAL_CLASS, criticalActive);
    document.body.classList.toggle(POPUP_CRITICAL_CLASS, criticalActive);

    if (delayActive && !delayReleaseTimeout) {
      delayReleaseTimeout = window.setTimeout(() => {
        delayReleaseTimeout = null;
        scheduleScan();
      }, INITIAL_POPUP_DELAY_MS - (Date.now() - startedAt) + 50);
    }
  }

  function refreshTimedSuppression() {
    setPopupStateClasses();
    updatePopupSuppression();

    if (criticalSuppressionTimeout) {
      window.clearTimeout(criticalSuppressionTimeout);
      criticalSuppressionTimeout = null;
    }

    const remaining = getRemainingTimedSuppression();
    if (remaining > 0) {
      criticalSuppressionTimeout = window.setTimeout(() => {
        criticalSuppressionTimeout = null;
        refreshTimedSuppression();
      }, remaining + 50);
    }
  }

  function beginCriticalSuppression(duration = CART_INTERACTION_SUPPRESSION_MS) {
    criticalSuppressionUntil = Math.max(criticalSuppressionUntil, Date.now() + duration);
    refreshTimedSuppression();
  }

  function beginCheckoutSuppression() {
    checkoutSuppressionUntil = Math.max(checkoutSuppressionUntil, Date.now() + CHECKOUT_SUPPRESSION_MS);
    refreshTimedSuppression();
  }

  function endCriticalSuppression() {
    criticalSuppressionUntil = Math.min(criticalSuppressionUntil, Date.now());
    refreshTimedSuppression();
  }

  function releasePopupScrollLock() {
    if (!document.body || isCartDrawerOpen() || isBrowsingOverlayOpen()) return;

    if (document.body.style.overflow === 'hidden') document.body.style.removeProperty('overflow');
    if (document.documentElement.style.overflow === 'hidden') document.documentElement.style.removeProperty('overflow');
  }

  function getSuppressionReason(root) {
    if (isCheckoutPage()) return 'checkout-page';
    if (isCartPage()) return 'cart-page';
    if (isHighIntentShoppingPage()) return 'shopping-page';
    if (isCartDrawerOpen()) return 'cart-drawer';
    if (isCartNotificationOpen()) return 'cart-notification';
    if (isBrowsingOverlayOpen()) return 'theme-overlay';
    if (isTimedCriticalSuppressionActive()) return 'shopping-interaction';
    if (isInitialDelayActive()) return 'initial-delay';
    if (isSessionLimited(root) && !isTeaserReopenActive()) return 'session-limit';

    return '';
  }

  function suppressPopupRoot(root, reason) {
    if (!(root instanceof HTMLElement)) return;

    if (reason) {
      if (
        root.hasAttribute(SUPPRESSED_ATTR) &&
        root.getAttribute(SUPPRESSION_REASON_ATTR) === reason &&
        root.getAttribute('aria-hidden') === 'true' &&
        root.hasAttribute('inert')
      ) {
        releasePopupScrollLock();
        return;
      }

      if (!suppressedRootState.has(root)) {
        suppressedRootState.set(root, {
          ariaHidden: root.getAttribute('aria-hidden'),
          hadInert: root.hasAttribute('inert'),
        });
      }

      root.setAttribute(SUPPRESSED_ATTR, '');
      root.setAttribute(SUPPRESSION_REASON_ATTR, reason);
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('inert', '');
      releasePopupScrollLock();
      return;
    }

    if (!root.hasAttribute(SUPPRESSED_ATTR)) {
      recordPopupShown(root);
      return;
    }

    const previousState = suppressedRootState.get(root);
    root.removeAttribute(SUPPRESSED_ATTR);
    root.removeAttribute(SUPPRESSION_REASON_ATTR);
    recordPopupShown(root);

    if (!previousState) return;

    if (previousState.ariaHidden === null) {
      root.removeAttribute('aria-hidden');
    } else {
      root.setAttribute('aria-hidden', previousState.ariaHidden);
    }

    if (!previousState.hadInert) root.removeAttribute('inert');
    suppressedRootState.delete(root);
  }

  function updatePopupSuppression() {
    setPopupStateClasses();
    document.querySelectorAll(`[${ROOT_ATTR}]`).forEach((root) => {
      suppressPopupRoot(root, getSuppressionReason(root));
    });
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('form').forEach(markPopup);
    updatePopupSuppression();
  }

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      scan(document);
    });
  }

  function handlePotentialShoppingEvent(event) {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('button, input, a, summary, [role="button"], form, modal-opener, quantity-input');
    if (!trigger || !isShoppingTrigger(trigger)) return;

    if (isCheckoutTrigger(trigger)) {
      beginCheckoutSuppression();
      return;
    }

    beginCriticalSuppression();
  }

  function handleSubmit(event) {
    if (isCheckoutTrigger(event.submitter)) {
      beginCheckoutSuppression();
      return;
    }

    if (isCartMutationForm(event.target)) beginCriticalSuppression();
  }

  function handleChange(event) {
    if (event.target instanceof Element && event.target.closest('quantity-input, cart-items, cart-drawer, quick-add-bulk, bulk-add')) {
      beginCriticalSuppression();
    }
  }

  function patchHistoryMethod(methodName) {
    const original = window.history?.[methodName];
    if (typeof original !== 'function') return;

    window.history[methodName] = function () {
      const result = original.apply(this, arguments);
      scheduleScan();
      return result;
    };
  }

  const observer = new MutationObserver(scheduleScan);

  window.addEventListener('wl:cart-drawer:open', () => {
    beginCriticalSuppression();
    updatePopupSuppression();
  });
  window.addEventListener('wl:cart-drawer:close', () => {
    beginCriticalSuppression(CART_DRAWER_CLOSE_SUPPRESSION_MS);
    updatePopupSuppression();
  });
  window.addEventListener('wl:cart-interaction:start', () => beginCriticalSuppression());
  window.addEventListener('wl:cart-interaction:end', endCriticalSuppression);
  window.addEventListener('popstate', scheduleScan);
  window.addEventListener('hashchange', scheduleScan);
  patchHistoryMethod('pushState');
  patchHistoryMethod('replaceState');

  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('pointerdown', handleTeaserActivation, true);
  document.addEventListener('click', handleTeaserActivation, true);
  document.addEventListener('pointerdown', handlePotentialShoppingEvent, true);
  document.addEventListener('click', handlePotentialShoppingEvent, true);
  document.addEventListener('keydown', (event) => {
    if (event.code !== 'Enter' && event.code !== 'Space') return;
    handlePotentialShoppingEvent(event);
  }, true);
  document.addEventListener('change', handleChange, true);
  document.addEventListener('toggle', scheduleScan, true);
  document.body?.addEventListener('modalClosed', () => beginCriticalSuppression(CART_DRAWER_CLOSE_SUPPRESSION_MS));

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scan(document);
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['open', 'class', 'style', 'aria-hidden'],
        });
      },
      { once: true }
    );
  } else {
    scan(document);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'class', 'style', 'aria-hidden'],
    });
  }
})();
